import React from "react";
import { CenteredSiteBlock } from "../../routes/Home";
import SectionTitle from "./SectionTitle";
import { Box, Grid, styled, Typography } from "@mui/material";
import PodcastVideoItem from "./PodcastVideoItem";
import IntervenantCard from "./IntervenantCard";
import RainbowDivider from "../misc/RainbowDivider";
import { useIsMobile } from "../../hooks/responsive";

const Container = styled(Box)(({ theme, backgroundImage }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    minHeight: "100px",
    position: "relative",
  };
});

const PodcastContent = ({
  podcast,
  trailerItem,
  videoItems,
  onClickWatch,
  onClickListen,
}) => {
  const isMobile = useIsMobile();

  const realisateurs = React.useMemo(() => {
    let tmp = null;
    try {
      tmp = JSON.parse(podcast.jsonRealisateurs);
    } catch (e) {
      console.error("Error with the realisateurs...");
    }
    return tmp;
  }, [podcast]);

  const animateurs = React.useMemo(() => {
    let tmp = null;
    try {
      tmp = JSON.parse(podcast.jsonAnimateurs);
    } catch (e) {
      console.error("Error with the animateurs...");
    }
    return tmp;
  }, [podcast]);

  const intervenants = React.useMemo(() => {
    let tmp = null;
    try {
      tmp = JSON.parse(podcast.jsonIntervenants);
    } catch (e) {
      console.error("Error with the intervenants...");
    }
    return tmp;
  }, [podcast]);

  return (
    <Container>
      <CenteredSiteBlock
        sx={{
          maxWidth: "1250px",
          display: "flex",
          flexDirection: "column",
          paddingTop: 2,
          paddingRight: 2,
          paddingLeft: 2,
          paddingBottom: 5,
        }}
      >
        {podcast.enBref && (
          <>
            <SectionTitle title={"En Bref"} />
            <Box
              dangerouslySetInnerHTML={{ __html: podcast.enBref }}
              sx={{
                width: "100%",
                textAlign: "left",
                fontWeight: 500,
                lineHeight: "1.75em",
                "& > P": {
                  marginTop: ".75em",
                },
              }}
            />
          </>
        )}
        {trailerItem && (
          <>
            <SectionTitle title={"Bande d'annonce"} />
            <Box
              sx={{
                marginTop: 1,
                display: "flex",
                width: "100%",
              }}
            >
              <PodcastVideoItem
                canListen={false}
                podcast={podcast}
                item={trailerItem}
                onClickWatch={() => {
                  onClickWatch(trailerItem);
                }}
                onClickListen={() => {
                  onClickListen(trailerItem);
                }}
              />
            </Box>
          </>
        )}
        {videoItems && videoItems.length > 0 && (
          <>
            <SectionTitle
              title={videoItems.length > 1 ? `Épisodes` : "Épisode"}
            />
            <Box
              sx={{
                marginTop: 1,
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              {videoItems.map((video) => (
                <PodcastVideoItem
                  podcast={podcast}
                  item={video}
                  onClickWatch={() => {
                    onClickWatch(video);
                  }}
                  onClickListen={() => {
                    onClickListen(video);
                  }}
                />
              ))}
            </Box>
          </>
        )}
        {!!intervenants && intervenants.length > 0 && (
          <>
            <SectionTitle title={"Intervenants"} />
            <Box
              sx={{
                display: "flex",
                width: "100%",
                alignItems: isMobile ? "center" : "flex-start",
                justifyContent: isMobile ? "center" : "flex-start",
                flexDirection: isMobile ? "column" : "row",
                gap: 3,
              }}
            >
              {intervenants.map((intervenant) => {
                return <IntervenantCard intervenant={intervenant} />;
              })}
            </Box>
          </>
        )}
        {(realisateurs?.length > 0 || animateurs?.length > 0) && (
          <>
            <SectionTitle title={"Fiche Technique"} />

            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                flexDirection: "row",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  aspectRatio: 215 / 290,
                  maxHeight: "300px",
                }}
              >
                <img
                  src={`https://medias.inrees.com/img/pro/audiovisuel/haut_${podcast?.id}.jpg`}
                  alt={"podcast image"}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
              <Box
                sx={{
                  marginLeft: 3,
                  display: "flex",
                  flexDirection: "column",
                  fontWeight: 900,
                }}
              >
                {realisateurs?.length > 0 && (
                  <>
                    <Typography
                      sx={{
                        color: "#182B43bb",
                      }}
                    >
                      {`Réalisateur${realisateurs?.length > 1 ? "s" : ""}`}
                    </Typography>
                    <Typography>
                      {realisateurs.reduce((acc, curr, index) => {
                        return (
                          acc +
                          `${index !== 0 ? ", " : ""}${curr.prenom} ${curr.nom}`
                        );
                      }, "")}
                    </Typography>
                  </>
                )}
                {animateurs?.length > 0 && (
                  <>
                    <Typography
                      sx={{
                        marginTop: 2,
                        color: "#182B43bb",
                      }}
                    >
                      {`Animateur${animateurs?.length > 1 ? "s" : ""}`}
                    </Typography>
                    <Box></Box>
                    <Typography 
                      sx={{
                        a: {
                          "&:hover": {
                            color: (theme) => theme.palette.primary.main
                          }
                        }
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: animateurs.reduce((acc, curr, index) => {
                          return (
                            acc +
                            `${index !== 0 ? ", " : ""}
                            <a href="https://www.inexplore.com/soutien/${curr.url}" target="_blank">${curr.prenom} ${curr.nom}</a>`
                          );
                        }, "")
                      }}
                    />
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
      </CenteredSiteBlock>
      <RainbowDivider />
    </Container>
  );
};

export default PodcastContent;
